// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * What a `.dsa` says about its stream, beyond its time axis.
 *
 * {@link file://./dsaTimeline.ts dsaTimeline.ts} reads the time axis a
 * stream declares; this reads the rest of the same file — who made it,
 * when it was last updated, what kind of product it is, and how complete
 * the series is, and the data credit the info panel shows. The catalog
 * row carries a title and a description; the .dsa is the only place the
 * rest is written. AYNI: the prompt text it also wrote for terraviz's
 * assistant went with that assistant (2026-10-03).
 *
 * Parsing is lenient where the timeline's is strict. A wrong date on a
 * forecast is worse than none, so the axis refuses a partial block; a
 * descriptor missing its creator is still worth describing, so each
 * field here is read on its own and simply left out when absent.
 */

import { parseRights, type StreamRights } from './rights'
import { parseColorKey, type ColorKey } from './colorKey'
import { offEarthBody } from './offEarthBody'
import { parsePlatforms, type DsaPlatform } from './satellites/sourcePlatforms'

export interface DsaCompleteness {
  /** `complete` | `partial` | … as written. */
  readonly status: string | null
  readonly expectedFrames: number | null
  readonly realFrames: number | null
  readonly filledFrames: number | null
  readonly missingFrames: number | null
  /** e.g. `freeze_previous_frame`. */
  readonly fillPolicy: string | null
  /** ISO timestamp of the publisher's last availability check. */
  readonly lastChecked: string | null
}

/** One source the publisher credits, with the licence its data came under. */
export interface DsaAttributionSource {
  readonly label: string | null
  readonly licenseName: string | null
  readonly licenseUrl: string | null
  readonly sourcePage: string | null
}

/**
 * AYNI — the credit the publisher asks to be shown with the data. For
 * CC BY sources (OSI SAF sea ice, GHSL, the IPCC atlases) showing it is
 * a condition of the licence, so it reaches the info panel and the
 * chatbot's context.
 */
export interface DsaAttribution {
  /** The credit sentence, as written. */
  readonly text: string
  /** Where the publisher asks for it: `panel` | `overlay`, as written. */
  readonly placement: string | null
  readonly sources: readonly DsaAttributionSource[]
}

export interface DsaMetadata {
  readonly title: string | null
  readonly description: string | null
  readonly creator: string | null
  /** ISO timestamps as written. */
  readonly created: string | null
  readonly modified: string | null
  /** `realtime` | `forecast` | `reanalysis` … as written. */
  readonly productType: string | null
  readonly categories: readonly string[]
  readonly keywords: readonly string[]
  readonly units: string | null
  readonly sourceUrl: string | null
  readonly completeness: DsaCompleteness | null
  readonly attribution: DsaAttribution | null
  /** AYNI — the reviewed rights block (`services/rights.ts`), null when absent or unusable. */
  readonly rights: StreamRights | null
  /**
   * AYNI: the descriptor lists the stream host's shared country borders
   * (`shared/country_borders_*.png`) as a visible layer over the data.
   */
  readonly declaresBorders: boolean
  /** AYNI: the picture layer's colour key (`alphaMode: "color_key"`, colorKey.ts), null for none. */
  readonly colorKey: ColorKey | null
  /**
   * AYNI (owner, 2026-10-14): the body the stream shows when it is not the
   * Earth, lower case, from a `celestialBody` field or a `body:<name>`
   * keyword (`body:io`; offEarthBody.ts); null for the Earth.
   */
  readonly celestialBody: string | null
  /**
   * AYNI (owner, 2026-10-23; DSA 1.10): the equipment the raw data came
   * from, the satellites first (`sourcePlatforms.ts`); empty for a
   * descriptor that names none.
   */
  readonly platforms: readonly DsaPlatform[]
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

function num(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.map(str).filter((s): s is string => s !== null) : []
}

/**
 * A field the schema localises as `{ en: "…", es: "…" }` — or, in an
 * older file, writes as a plain string. Prefers the requested language,
 * then English, then whatever the file has.
 */
function localized(value: unknown, lang: string): string | null {
  const plain = str(value)
  if (plain) return plain
  const record = asRecord(value)
  if (!record) return null
  const base = lang.toLowerCase().split('-')[0] ?? 'en'
  return str(record[base]) ?? str(record.en) ?? Object.values(record).map(str).find(Boolean) ?? null
}

/** Keywords that name the encoding rather than the data — noise to a visitor. */
const ENCODING_KEYWORDS = new Set(['vp9', 'vp8', 'av1', 'h264', 'h265', 'hevc', 'dash', 'hls', 'mp4', 'webm'])

/**
 * The descriptor's metadata, or null for something that is not a
 * descriptor at all. Never throws.
 */
export function parseDsaMetadata(raw: unknown, lang = 'en'): DsaMetadata | null {
  const dsa = asRecord(raw)
  if (!dsa) return null
  const availability = asRecord(dsa.dataAvailability)
  const links = asRecord(dsa.links)
  const credit = asRecord(dsa.attribution)
  const creditText = str(credit?.text)
  return {
    title: localized(dsa.title, lang),
    description: localized(dsa.description, lang),
    creator: str(dsa.creator),
    created: str(dsa.created),
    modified: str(dsa.modified),
    productType: str(dsa.dataProductType),
    categories: strings(dsa.categories),
    keywords: strings(dsa.keywords).filter(k => !ENCODING_KEYWORDS.has(k.toLowerCase())),
    units: str(dsa.units),
    declaresBorders: (Array.isArray(dsa.layers) ? dsa.layers : []).some((entry) => {
      const layer = asRecord(entry)
      return !!layer && layer.isLegend !== true && layer.visible !== false && /(^|\/)shared\/country_borders_[a-z]+_v\d+\.png$/i.test(str(layer.path) ?? '')
    }),
    colorKey: parseColorKey(dsa),
    celestialBody: offEarthBody({ celestialBody: dsa.celestialBody, keywords: Array.isArray(dsa.keywords) ? dsa.keywords : [] }),
    platforms: parsePlatforms(dsa),
    sourceUrl: str(links?.source),
    rights: parseRights(dsa.rights),
    completeness: availability
      ? {
          status: str(availability.status),
          expectedFrames: num(availability.expectedFrameCount),
          realFrames: num(availability.realFrameCount),
          filledFrames: num(availability.filledFrameCount),
          missingFrames: num(availability.missingFrameCount),
          fillPolicy: str(availability.fillPolicy),
          lastChecked: str(availability.lastChecked),
        }
      : null,
    attribution: credit && creditText
      ? {
          text: creditText,
          placement: str(credit.placement),
          sources: (Array.isArray(credit.sources) ? credit.sources : []).flatMap((entry): DsaAttributionSource[] => {
            const source = asRecord(entry)
            return source
              ? [{
                  label: str(source.label),
                  licenseName: str(source.licenseName),
                  licenseUrl: str(source.license),
                  sourcePage: str(source.sourcePage),
                }]
              : []
          }),
        }
      : null,
  }
}
